import { Component, CUSTOM_ELEMENTS_SCHEMA, Input, EventEmitter, Output } from '@angular/core';
import { RouterModule } from '@angular/router';

import { FormGroup } from '@angular/forms';
import { debounceTime, distinctUntilChanged, ReplaySubject, takeUntil } from 'rxjs';
import '@maersk-global/mds-components-core/mc-pagination';
import '@maersk-global/mds-components-core/mc-loading-indicator';
import '@maersk-global/mds-components-core/mc-button';
import '@maersk-global/mds-components-core/mc-icon';
import '@maersk-global/mds-components-core/mc-table';
import { utils } from '../../../helper/utils';
import { WorkOrderService } from '../../../services/work-order.service';
import { MaintenanceOrderHeader } from '../../../models/maintenance-order-header.model';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { WorkOrderFilterForm } from '../../../form-model/work-order-filter.form.model';
import { MaintenanceOrderFilter } from '../../../models/maintenance-order-filter.model';
import { WorkOrderSearchTextForm } from '../../../form-model/work-order-search-text.form.model';
import { LoaderComponent } from '../../../shared-components/loader/loader.component';
import { APICallStatus } from '../../../models/api-call-status.mode';
import { BulkWorkOrderCompletionBody } from '../../../models/bulk-work-order/bulk-work-order-completion.model';
import { SharedDataService } from '../../../services/shared-data.service';
import { workOrderListGridColumns } from './work-order-grid.config';

@Component({
  selector: 'app-work-order-grid',
  imports: [NgxSkeletonLoaderModule, RouterModule, LoaderComponent],
  templateUrl: './work-order-grid.component.html',
  styleUrl: './work-order-grid.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class WorkOrderGridComponent {
  selected: string[] = [];
  selectedRows: BulkWorkOrderCompletionBody[] = [];
  data: MaintenanceOrderHeader[] = [];
  mockDataForLoadingAnimation: { id: number }[] = [...Array(30).keys()].map((i) => ({ id: i }));
  firstPage: number | undefined;
  previousPage: number | undefined;
  currentPage: number = 1;
  nextPage: number | undefined;
  lastPage: number | undefined;
  totalItemCount: number | undefined;
  apiVersion: string | undefined;
  pageLimit: number = 20;
  sort: string | undefined;
  filterParam: MaintenanceOrderFilter = {};
  getWorkOrdersApiCallStatus: APICallStatus = 'NOT_STARTED';
  hasData: boolean = false;
  urlNavigate: string | undefined;
  @Input({ required: true }) filterFormGroup!: FormGroup<{
    workOrderFilterForm: FormGroup<WorkOrderFilterForm>;
    workOrderSearchTextForm: FormGroup<WorkOrderSearchTextForm>;
  }>;
  @Output() selectedRowsChange = new EventEmitter<BulkWorkOrderCompletionBody[]>(); // Emit the selected rows
  columns = workOrderListGridColumns;

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  @Input() isWorkOrderList: boolean = true;
  constructor(
    private workOrderService: WorkOrderService,
    private sharedDataService: SharedDataService,
  ) {}

  ngOnInit() {
    this.sort = `createTimestamp:desc,updateTimestamp:desc`;
    this.urlNavigate = '/workorders';
    this.subscribeToFormChanges();
    this.filterParam.isProcessedByRuleEngine = !this.isWorkOrderList;
    // fetch work orders for if work order page, else for approval screen work orders with default filters need to fetched
    if (this.isWorkOrderList && !this.sharedDataService.getWorkOrderFilterFormValue()) {
      this.getWorkOrder();
    }
  }
  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  private subscribeToFormChanges() {
    this.filterFormGroup.valueChanges.pipe(debounceTime(500), distinctUntilChanged()).subscribe(() => {
      if (
        this.filterFormGroup.controls.workOrderFilterForm.controls.applyWOFilter.value ||
        this.filterFormGroup.controls.workOrderSearchTextForm.controls.appliedWOSearchTextFilter.value
      ) {
        this.onApplyFilter();
      }
    });
  }
  private syncSelectedWithPageData(): void {
    const selectedIds = new Set(this.selectedRows.map((r) => String(r.workOrderId)));

    this.selected = this.data
      .filter((row) => selectedIds.has(String(row.maintenanceOrderNumber)))
      .map((row) => String(row.maintenanceOrderNumber));
  }

  onRowSelect(event: Event) {
    let customEvent = event as CustomEvent;
    let selectedData = customEvent.detail; // Now we directly access the object

    if (selectedData && selectedData.maintenanceOrderNumber) {
      let orderNumber = selectedData.maintenanceOrderNumber;
      let statusCode = selectedData.maintenanceOrderStatusCode;

      // Add to selectedRows only if not already present
      if (!this.selectedRows.some((r) => r.workOrderId === +orderNumber)) {
        this.selectedRows.push({ workOrderId: +orderNumber, statusCode });
      }

      this.syncSelectedWithPageData();
      this.selectedRowsChange.emit(this.selectedRows); //emitting selected rows
      //console.log("number of selected rows:", this.selectedRows.length);
    }
  }
  onRowDeselect(event: Event) {
    let customEvent = event as CustomEvent;
    let deselectedData = customEvent.detail; // Single object

    if (deselectedData && deselectedData.maintenanceOrderNumber) {
      let orderNumber = deselectedData.maintenanceOrderNumber;

      // Remove from selectedRows
      if (this.selectedRows.some((row) => row.workOrderId === orderNumber)) {
        this.selectedRows = this.selectedRows.filter((row) => row.workOrderId !== +orderNumber);
      }

      this.syncSelectedWithPageData();
      this.selectedRowsChange.emit(this.selectedRows);
      //console.log("number of selected rows after deselection:", this.selectedRows.length);
    }
  }
  onSortDirectionChange(event: Event) {
    let customEvent = event as CustomEvent;
    let sortColumn: string = this.sortColumnName(customEvent.detail.column);
    let sortDirection: string = customEvent.detail.direction == 'ascending' ? 'asc' : 'desc';
    if (sortColumn != 'id') {
      this.sort = `${sortColumn}:${sortDirection}`;
      this.currentPage = 1;
      this.getWorkOrder();
    }
  }
  private sortColumnName(gridColumnName: string): string {
    switch (gridColumnName) {
      case 'maintenanceOrderStatusDuration':
        return 'maintenanceOrderStatusUpdatedTimestamp';
      case 'totalOrderCostUSD':
        return 'totalOrderCost';
      default:
        return gridColumnName;
    }
  }

  onPageLimitChange(event: Event) {
    let customEvent = event as CustomEvent;
    this.currentPage = 1;
    this.pageLimit = customEvent.detail.value;

    this.getWorkOrder();
  }

  onPageChange(event: Event) {
    let customEvent = event as CustomEvent;
    let changedPage = customEvent.detail as number;
    if (changedPage != this.currentPage) {
      this.currentPage = changedPage;
      this.getWorkOrder(true);
    }
  }

  onApplyFilter() {
    if (this.filterFormGroup.controls.workOrderFilterForm.valid) {
      const filters: MaintenanceOrderFilter = {};

      if (this.filterFormGroup.controls.workOrderFilterForm.controls.equipmentId.value) {
        filters.equipmentNumber = this.filterFormGroup.controls.workOrderFilterForm.controls.equipmentId.value;
      }
      if (
        this.filterFormGroup.controls.workOrderFilterForm.controls.mode.value &&
        this.filterFormGroup.controls.workOrderFilterForm.controls.mode.value.length > 0
      ) {
        filters.maintenanceRepairModes = this.filterFormGroup.controls.workOrderFilterForm.controls.mode.value.map((x) => x.code).join(',');
      }
      if (this.filterFormGroup.controls.workOrderFilterForm.controls.woNumber.value) {
        filters.maintenanceOrderNumber = this.filterFormGroup.controls.workOrderFilterForm.controls.woNumber.value;
      }
      if (
        this.filterFormGroup.controls.workOrderFilterForm.controls.status.value &&
        this.filterFormGroup.controls.workOrderFilterForm.controls.status.value.length > 0
      ) {
        filters.maintenanceOrderStatusTypes = this.filterFormGroup.controls.workOrderFilterForm.controls.status.value
          .map((x) => x.generatedCode?.replace(/\-/g, ','))
          .join(',');
      }
      if (this.filterFormGroup.controls.workOrderFilterForm.controls.fromDate.value) {
        filters.fromDate = this.filterFormGroup.controls.workOrderFilterForm.controls.fromDate.value;
      }
      if (this.filterFormGroup.controls.workOrderFilterForm.controls.toDate.value) {
        filters.toDate = this.filterFormGroup.controls.workOrderFilterForm.controls.toDate.value;
      }
      if (
        this.filterFormGroup.controls.workOrderFilterForm.controls.shop.value &&
        this.filterFormGroup.controls.workOrderFilterForm.controls.shop.value.length > 0
      ) {
        filters.maintenanceShopCodes = this.filterFormGroup.controls.workOrderFilterForm.controls.shop.value.map((x) => x).join(',');
      }
      if (this.filterFormGroup.controls.workOrderSearchTextForm.controls.searchText.value) {
        filters.searchText = utils.normalizeSearchTextForApi(this.filterFormGroup.controls.workOrderSearchTextForm.controls.searchText.value);
      }
      if (this.filterFormGroup.controls.workOrderFilterForm.controls.showFlaggedWO.value) {
        filters.showFlaggedWO = this.filterFormGroup.controls.workOrderFilterForm.controls.showFlaggedWO.value;
      }
      this.filterParam = filters;
      this.currentPage = 1;
      this.getWorkOrder();
    }
  }

  getWorkOrder(isPageChange: boolean = false): void {
    this.getWorkOrdersApiCallStatus = 'IN_PROGRESS';
    this.filterParam.isProcessedByRuleEngine = !this.isWorkOrderList;
    this.workOrderService
      .getWorkOrders(this.currentPage, this.pageLimit, this.sort, this.filterParam)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (response) => {
          const headers = response.headers;
          const body = response.body || [];
          this.data = body;
          this.firstPage = headers.get('First-Page') ? +headers.get('First-Page')! : undefined;
          this.previousPage = headers.get('Previous-Page') ? +headers.get('Previous-Page')! : undefined;
          this.currentPage = +headers.get('Current-Page')!;
          this.nextPage = headers.get('Next-Page') ? +headers.get('Next-Page')! : undefined;
          this.lastPage = headers.get('Last-Page') ? +headers.get('Last-Page')! : undefined;
          this.totalItemCount = headers.get('Total-Item-Count') ? +headers.get('Total-Item-Count')! : undefined;
          this.apiVersion = headers.get('API-Version') ?? undefined;
          this.getWorkOrdersApiCallStatus = 'COMPLETED_SUCCESFULLY';
          this.syncSelectedWithPageData();
          // Reset selected rows when data is fetched and its not a page change
          if (!isPageChange) {
            this.selectedRows = [];
            this.selectedRowsChange.emit(this.selectedRows);
            this.selected = []; // clear the selection on the grid
          }
        },
        error: (err) => {
          this.getWorkOrdersApiCallStatus = 'FAILED';
          this.data = [];
          if (!isPageChange) {
            this.selectedRows = [];
            this.selectedRowsChange.emit(this.selectedRows);
            this.selected = []; // clear the selection on the grid
          }
        },
      });
  }

  clearFilter() {
    this.filterFormGroup.reset();
    this.filterFormGroup.controls.workOrderSearchTextForm.controls.appliedWOSearchTextFilter.setValue(true);
    this.filterFormGroup.controls.workOrderFilterForm.controls.applyWOFilter.setValue(true);
  }
}
